import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Stage, Layer, Line, Rect, Text, Group, Circle, Ellipse, Transformer, Image as KonvaImage } from 'react-konva';
import { levelerIconFor } from './levelerIcons';

function useHtmlImage(src) {
  const [image, setImage] = useState(null);
  useEffect(() => {
    if (!src) { setImage(null); return; }
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.src = src;
    const handleLoad = () => setImage(img);
    img.addEventListener('load', handleLoad);
    return () => img.removeEventListener('load', handleLoad);
  }, [src]);
  return image;
}

const GRID_SIZE = 20;
const DEFAULT_GATE_WIDTH = 64;
const DEFAULT_GATE_HEIGHT = 78;
const ICON_SIZE = 56;
const MIN_GATE_SCALE = 0.4;
const MAX_GATE_SCALE = 4;

const LINE_COLORS = [
  { value: '#1a1a2e', label: 'שחור' },
  { value: '#4a5568', label: 'אפור' },
  { value: '#3b82f6', label: 'כחול' },
  { value: '#8b5cf6', label: 'סגול' },
  { value: '#ef4444', label: 'אדום' },
  { value: '#22c55e', label: 'ירוק' },
  { value: '#a16207', label: 'חום' },
];

const LINE_WIDTHS = [
  { value: 2, label: 'דק' },
  { value: 4, label: 'רגיל' },
  { value: 6, label: 'עבה' },
  { value: 10, label: 'עבה מאוד' },
];

// Color gradient for the live timer / status dot.
// When a gate has no long-open threshold configured, customers told us
// they don't want a "warning" color escalation — it's just informative.
// In that case we render the timer in a neutral grey.
function getOpenStateColor(openMinutes, thresholdMinutes) {
  if (thresholdMinutes == null) return '#6b7280'; // neutral grey
  const ratio = openMinutes / thresholdMinutes;
  if (ratio < 0.33) return '#22c55e'; // green
  if (ratio < 0.66) return '#eab308'; // yellow
  if (ratio < 1.0)  return '#ef4444'; // red
  return '#dc2626';                    // bright red — past threshold
}

function formatOpenDuration(openSeconds) {
  if (openSeconds < 60) return `${Math.floor(openSeconds)}ש'`; // "Xש'" — seconds
  const minutes = Math.floor(openSeconds / 60);
  if (minutes < 60) return `${minutes}'`;
  const hours = Math.floor(minutes / 60);
  const remMin = minutes % 60;
  return `${hours}:${String(remMin).padStart(2, '0')}`;
}

function SiteMapCanvas({
  gates, layout, editable,
  onLayoutChange, onGateMove, onGateClick, onGateResize, onMarkerUpdate,
  selectionMode = false,
  selectedGateIds = [],
  showLiveTimer = false,
  // Optional override (Phase 4 PR 3.B): when supplied, the gate gets
  // a coloured ring around the card with the returned colour. Used by
  // the technician site map to paint installation status without
  // disturbing the existing customer/admin live colours.
  statusRingColorFor = null,
  // Delete-from-map (edit mode): ``onGateDelete(gate)`` performs the
  // delete; ``gateDeletable(gate)`` decides whether the ✕ badge shows.
  // Customer passes a predicate (un-installed only); admin allows all.
  onGateDelete = null,
  gateDeletable = null,
}) {
  const containerRef = useRef(null);
  const stageRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 500 });
  const [tool, setTool] = useState('select');
  const [walls, setWalls] = useState(layout?.walls || []);
  const [markers, setMarkers] = useState(layout?.markers || []);
  const [drawing, setDrawing] = useState(null);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const [stageScale, setStageScale] = useState(1);
  // Two-finger pinch state. We hold the previous touch midpoint and
  // distance between frames so each touchmove tick can compute the
  // delta. Lives on refs (not state) so the gesture is jank-free —
  // setState in a touchmove handler would re-render the whole canvas
  // on every frame.
  const pinchLastDist = useRef(0);
  const pinchLastCenter = useRef(null);
  const [lineColor, setLineColor] = useState('#1a1a2e');
  const [lineWidth, setLineWidth] = useState(4);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [selectedId, setSelectedId] = useState(null); // 'gate-3' or 'tree-5'
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (layout?.walls) setWalls(layout.walls);
    if (layout?.markers) setMarkers(layout.markers);
  }, [layout]);

  // Local clock tick for the live timer over each open gate. We only
  // arm the interval when there's actually something to count, so a
  // map with all gates closed is idle.
  const [nowMs, setNowMs] = useState(() => Date.now());
  const hasAnyOpenGate = showLiveTimer && gates.some(g => g.is_open && g.opened_at && g.live_timer_enabled);
  useEffect(() => {
    if (!hasAnyOpenGate) return undefined;
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [hasAnyOpenGate]);

  // First-load auto-fit: in view mode (the customer's live map) we
  // size the stage so the entire layout — walls, gates, tree markers —
  // fits inside the viewport with a small margin. In edit mode we
  // keep the natural 1:1 origin so admins can place elements with
  // pixel precision. Manual pan/zoom afterwards is preserved.
  //
  // We hold off until ``dimensionsMeasured`` flips — the initial
  // ``dimensions`` state is a placeholder, and fitting against it
  // produces a transform that doesn't match the real container
  // (especially noticeable on phones, where the placeholder is
  // wider than the actual screen and the map ends up off-canvas).
  const [hasAutoFitted, setHasAutoFitted] = useState(false);
  const [dimensionsMeasured, setDimensionsMeasured] = useState(false);
  const fitToContent = useCallback(() => {
    if (dimensions.width <= 0 || dimensions.height <= 0) return false;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    walls.forEach(w => {
      minX = Math.min(minX, w.x1, w.x2);
      minY = Math.min(minY, w.y1, w.y2);
      maxX = Math.max(maxX, w.x1, w.x2);
      maxY = Math.max(maxY, w.y1, w.y2);
    });
    gates.forEach(g => {
      const x = g.position_x || 0;
      const y = g.position_y || 0;
      const s = g.gate_scale || 1;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + DEFAULT_GATE_WIDTH * s);
      maxY = Math.max(maxY, y + DEFAULT_GATE_HEIGHT * s);
    });
    markers.forEach(m => {
      const x = m.x || 0;
      const y = m.y || 0;
      const s = m.scale || 1;
      const r = 30 * s;
      minX = Math.min(minX, x - r);
      minY = Math.min(minY, y - r);
      maxX = Math.max(maxX, x + r);
      maxY = Math.max(maxY, y + r);
    });

    if (!isFinite(minX)) return false;
    const contentW = maxX - minX;
    const contentH = maxY - minY;
    if (contentW <= 0 || contentH <= 0) return false;

    const pad = 40;
    const availW = Math.max(1, dimensions.width - pad * 2);
    const availH = Math.max(1, dimensions.height - pad * 2);
    const newScale = Math.min(availW / contentW, availH / contentH, 1.5);

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    setStageScale(newScale);
    setStagePos({
      x: dimensions.width / 2 - centerX * newScale,
      y: dimensions.height / 2 - centerY * newScale,
    });
    return true;
  }, [walls, gates, markers, dimensions]);

  useEffect(() => {
    if (editable || hasAutoFitted) return;
    if (!dimensionsMeasured) return;
    if (fitToContent()) setHasAutoFitted(true);
  }, [editable, hasAutoFitted, dimensionsMeasured, fitToContent]);

  // Re-measure the canvas whenever the container (not just the window)
  // changes size. Phase 5.G PR-3 fixes "the map smears when scrolling /
  // toggling list↔map":
  //
  //   1. The previous code measured ``container.offsetHeight`` as a
  //      lower bound for the stage height. But the container's height
  //      is driven *by* the Stage element it contains — so every
  //      re-measure grew the canvas (Stage → container → next read →
  //      taller Stage). That feedback loop is what "smearing" looked
  //      like. We now use a fixed height per breakpoint and never read
  //      the container's height at all.
  //
  //   2. The previous code listened only to ``window.resize``. When the
  //      list/map mobile toggle showed the map for the first time, or
  //      when the slide panel opened and reflowed the layout, the
  //      container's width changed without a window event — so the
  //      Stage kept whatever placeholder it had at mount. A
  //      ResizeObserver on the container catches every such change.
  useEffect(() => {
    const computeDimensions = () => {
      if (isFullscreen) {
        return { width: window.innerWidth, height: window.innerHeight };
      }
      const el = containerRef.current;
      if (!el) return null;
      const isMobile = window.innerWidth < 768;
      const width = el.offsetWidth;
      // Fixed height per breakpoint — see comment above for why we
      // intentionally don't read ``el.offsetHeight``.
      const height = isMobile ? 340 : 500;
      return { width, height };
    };

    const apply = () => {
      const next = computeDimensions();
      if (!next || next.width <= 0) return;
      setDimensions(prev => (prev.width === next.width && prev.height === next.height)
        ? prev
        : next);
      setDimensionsMeasured(true);
    };

    apply();

    let resizeObserver = null;
    if (containerRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(apply);
      resizeObserver.observe(containerRef.current);
    }
    window.addEventListener('resize', apply);
    return () => {
      window.removeEventListener('resize', apply);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [isFullscreen]);

  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isFullscreen]);

  const snapToGrid = (val) => Math.round(val / GRID_SIZE) * GRID_SIZE;

  const getPointerPos = useCallback((stage) => {
    const pos = stage.getPointerPosition();
    const transform = stage.getAbsoluteTransform().copy().invert();
    const point = transform.point(pos);
    return { x: snapToGrid(point.x), y: snapToGrid(point.y) };
  }, []);

  const saveLayout = useCallback((newWalls, newMarkers) => {
    if (onLayoutChange) {
      onLayoutChange({ walls: newWalls, markers: newMarkers });
    }
  }, [onLayoutChange]);

  const handleStageMouseDown = useCallback((e) => {
    // Deselect when clicking on empty area
    const clickedOnEmpty = e.target === e.target.getStage() || e.target.getParent() === e.target.getStage();
    if (clickedOnEmpty && editable) {
      setSelectedId(null);
    }

    if (!editable) return;
    const stage = e.target.getStage();
    const pos = getPointerPos(stage);

    if (tool === 'wall') {
      setDrawing({ x1: pos.x, y1: pos.y, x2: pos.x, y2: pos.y });
    } else if (tool === 'tree') {
      const newMarkers = [...markers, { type: 'tree', x: pos.x, y: pos.y, scale: 1 }];
      setMarkers(newMarkers);
      saveLayout(walls, newMarkers);
    } else if (tool === 'road') {
      setDrawing({ type: 'road', x1: pos.x, y1: pos.y, x2: pos.x, y2: pos.y });
    }
  }, [editable, tool, getPointerPos, markers, walls, saveLayout]);

  const handleMouseMove = useCallback((e) => {
    if (!drawing) return;
    const stage = e.target.getStage();
    const pos = getPointerPos(stage);
    setDrawing(prev => ({ ...prev, x2: pos.x, y2: pos.y }));
  }, [drawing, getPointerPos]);

  const handleMouseUp = useCallback(() => {
    if (!drawing) return;
    const dx = Math.abs(drawing.x2 - drawing.x1);
    const dy = Math.abs(drawing.y2 - drawing.y1);

    if (dx > 10 || dy > 10) {
      if (drawing.type === 'road') {
        const newWalls = [...walls, { x1: drawing.x1, y1: drawing.y1, x2: drawing.x2, y2: drawing.y2, color: '#9ca3af', width: 12, dash: true }];
        setWalls(newWalls);
        saveLayout(newWalls, markers);
      } else {
        const newWalls = [...walls, { x1: drawing.x1, y1: drawing.y1, x2: drawing.x2, y2: drawing.y2, color: lineColor, width: lineWidth }];
        setWalls(newWalls);
        saveLayout(newWalls, markers);
      }
    }
    setDrawing(null);
  }, [drawing, walls, markers, lineColor, lineWidth, saveLayout]);

  // Phase 5.G PR-6 / PR-8 — two-finger pinch-zoom on touch devices.
  //
  // PR-6 attached this via react-konva's ``onTouchMove`` prop, but
  // react-konva wires touch listeners as PASSIVE, which makes
  // ``preventDefault()`` a no-op — the browser then handled the pinch
  // itself and our handler never got to mutate the stage. PR-8 fixes
  // it by attaching the native listener directly to the Konva
  // container with ``{ passive: false }`` so we own the gesture.
  //
  // We also mutate the Konva stage DIRECTLY during the gesture
  // (``stage.scaleX(s); stage.position(p); stage.batchDraw()``) and
  // only sync to React state on touchend. Setting state on every
  // touchmove tick (every ~16ms) caused noticeable jank on older
  // phones, and React's batching can leave ``stage.scaleX()`` lagging
  // a frame behind on hot gestures.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    const container = stage.container();
    if (!container) return undefined;

    const onTouchMove = (evt) => {
      const touches = evt.touches;
      if (!touches || touches.length < 2) return;
      evt.preventDefault();
      if (stage.isDragging()) stage.stopDrag();

      const rect = container.getBoundingClientRect();
      const p1 = { x: touches[0].clientX - rect.left, y: touches[0].clientY - rect.top };
      const p2 = { x: touches[1].clientX - rect.left, y: touches[1].clientY - rect.top };
      const newCenter = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
      const newDist = Math.hypot(p1.x - p2.x, p1.y - p2.y);

      if (!pinchLastCenter.current) {
        pinchLastCenter.current = newCenter;
        pinchLastDist.current = newDist;
        return;
      }

      const oldScale = stage.scaleX();
      const pointTo = {
        x: (newCenter.x - stage.x()) / oldScale,
        y: (newCenter.y - stage.y()) / oldScale,
      };

      const ratio = pinchLastDist.current ? newDist / pinchLastDist.current : 1;
      const clampedScale = Math.max(0.3, Math.min(3, oldScale * ratio));

      const dx = newCenter.x - pinchLastCenter.current.x;
      const dy = newCenter.y - pinchLastCenter.current.y;

      const newPos = {
        x: newCenter.x - pointTo.x * clampedScale + dx,
        y: newCenter.y - pointTo.y * clampedScale + dy,
      };

      stage.scaleX(clampedScale);
      stage.scaleY(clampedScale);
      stage.position(newPos);
      stage.batchDraw();

      pinchLastDist.current = newDist;
      pinchLastCenter.current = newCenter;
    };

    const onTouchEnd = (evt) => {
      // When the gesture ends (or drops to 1 touch), sync the
      // Konva-side scale/position back to React state so the next
      // render keeps the new values and subsequent +/- buttons
      // pan/zoom from the right baseline.
      if (pinchLastCenter.current) {
        setStageScale(stage.scaleX());
        setStagePos({ x: stage.x(), y: stage.y() });
      }
      if (!evt.touches || evt.touches.length < 2) {
        pinchLastCenter.current = null;
        pinchLastDist.current = 0;
      }
    };

    // passive:false is the whole point — without it preventDefault is
    // silently ignored and the browser keeps handling pinch itself.
    container.addEventListener('touchmove', onTouchMove, { passive: false });
    container.addEventListener('touchend', onTouchEnd);
    container.addEventListener('touchcancel', onTouchEnd);
    return () => {
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
      container.removeEventListener('touchcancel', onTouchEnd);
    };
  }, []);

  const zoomBy = useCallback((factor) => {
    const newScale = Math.max(0.3, Math.min(3, stageScale * factor));
    if (newScale === stageScale) return;
    const center = { x: dimensions.width / 2, y: dimensions.height / 2 };
    const focusPoint = {
      x: (center.x - stagePos.x) / stageScale,
      y: (center.y - stagePos.y) / stageScale,
    };
    setStageScale(newScale);
    setStagePos({
      x: center.x - focusPoint.x * newScale,
      y: center.y - focusPoint.y * newScale,
    });
  }, [stageScale, stagePos, dimensions]);

  const resetView = useCallback(() => {
    // In view mode the reset button means "show me everything again"
    // (auto-fit). In edit mode it means "back to 1:1" — admins are
    // working in absolute coordinates and a fit would surprise them.
    if (!editable && fitToContent()) return;
    setStageScale(1);
    setStagePos({ x: 0, y: 0 });
  }, [editable, fitToContent]);

  const handleDeleteWall = (index) => {
    const newWalls = walls.filter((_, i) => i !== index);
    setWalls(newWalls);
    saveLayout(newWalls, markers);
  };

  const handleDeleteMarker = (index) => {
    const newMarkers = markers.filter((_, i) => i !== index);
    setMarkers(newMarkers);
    setSelectedId(null);
    saveLayout(walls, newMarkers);
  };

  const handleMarkerTransform = (index, newScale) => {
    const newMarkers = markers.map((m, i) => i === index ? { ...m, scale: newScale } : m);
    setMarkers(newMarkers);
    saveLayout(walls, newMarkers);
  };

  const handleGateDragEnd = (gate, e) => {
    if (!editable || !onGateMove) return;
    const pos = { x: snapToGrid(e.target.x()), y: snapToGrid(e.target.y()) };
    onGateMove(gate.id, pos.x, pos.y);
  };

  const handleClearAll = () => {
    setWalls([]);
    setMarkers([]);
    setSelectedId(null);
    saveLayout([], []);
  };

  const getOpenSeconds = (gate) => {
    if (!gate.is_open || !gate.opened_at) return 0;
    return Math.max(0, (nowMs - new Date(gate.opened_at).getTime()) / 1000);
  };

  const getGateColor = (gate) => {
    if (gate.is_open) {
      const minutes = getOpenSeconds(gate) / 60;
      return getOpenStateColor(minutes, gate.long_open_threshold_minutes);
    }
    if (gate.cycle_limit && gate.cycle_count / gate.cycle_limit >= 0.9) return '#eab308';
    return '#22c55e';
  };

  const isDraggable = tool === 'select';

  return (
    <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
      {editable && (
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <ToolButton active={tool === 'select'} onClick={() => setTool('select')} label="בחירה" />
            <ToolButton active={tool === 'wall'} onClick={() => setTool('wall')} label="צייר קיר" />
            <ToolButton active={tool === 'road'} onClick={() => setTool('road')} label="כביש" />
            <ToolButton active={tool === 'tree'} onClick={() => setTool('tree')} label="עץ" />

            <div style={{ width: '1px', height: '24px', background: '#e5e7eb', margin: '0 0.4rem' }} />

            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowColorPicker(!showColorPicker)}
                style={{ width: '28px', height: '28px', borderRadius: '6px', border: '1px solid #e5e7eb', background: lineColor, cursor: 'pointer' }}
              />
              {showColorPicker && (
                <div style={{ position: 'absolute', top: '34px', right: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '0.5rem', display: 'flex', gap: '0.3rem', zIndex: 10 }}>
                  {LINE_COLORS.map(c => (
                    <button
                      key={c.value}
                      onClick={() => { setLineColor(c.value); setShowColorPicker(false); }}
                      title={c.label}
                      style={{ width: '24px', height: '24px', borderRadius: '4px', border: lineColor === c.value ? '2px solid #1a1a2e' : '1px solid #e5e7eb', background: c.value, cursor: 'pointer' }}
                    />
                  ))}
                </div>
              )}
            </div>

            <select
              value={lineWidth}
              onChange={e => setLineWidth(Number(e.target.value))}
              style={{ padding: '0.3rem 0.5rem', borderRadius: '6px', border: '1px solid #e5e7eb', fontSize: '0.8rem', fontFamily: 'Heebo, sans-serif', cursor: 'pointer' }}
            >
              {LINE_WIDTHS.map(w => (
                <option key={w.value} value={w.value}>{w.label}</option>
              ))}
            </select>

            <div style={{ marginRight: 'auto' }} />

            {(walls.length > 0 || markers.length > 0) && (
              <button className="btn btn-secondary btn-sm" onClick={handleClearAll}>נקה הכל</button>
            )}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.35rem' }}>
            {tool === 'select' && 'לחץ על שער או עץ כדי לבחור, ואז גרור פינה לשינוי גודל.'}
            {tool === 'wall' && 'לחץ וגרור לציור קיר. דאבל-קליק על קיר למחיקה.'}
            {tool === 'tree' && 'לחץ על המפה להנחת עץ. דאבל-קליק על עץ למחיקה.'}
            {tool === 'road' && 'לחץ וגרור לציור כביש. דאבל-קליק על כביש למחיקה.'}
          </div>
        </div>
      )}

      <div style={{
        position: isFullscreen ? 'fixed' : 'relative',
        top: isFullscreen ? 0 : 'auto',
        left: isFullscreen ? 0 : 'auto',
        width: isFullscreen ? '100vw' : 'auto',
        height: isFullscreen ? '100vh' : 'auto',
        zIndex: isFullscreen ? 9999 : 'auto',
        border: isFullscreen ? 'none' : '1px solid #e5e7eb',
        borderRadius: isFullscreen ? 0 : '6px',
        overflow: 'hidden',
        background: '#ffffff',
        cursor: tool === 'wall' || tool === 'road' ? 'crosshair' : tool === 'tree' ? 'cell' : 'grab',
        // Block the browser's native pinch-zoom / double-tap-zoom so
        // the canvas can handle both gestures. Single-finger pan still
        // works through Konva's draggable.
        touchAction: 'none',
      }}>
        <Stage
          ref={stageRef}
          width={dimensions.width}
          height={dimensions.height}
          scaleX={stageScale}
          scaleY={stageScale}
          x={stagePos.x}
          y={stagePos.y}
          draggable={isDraggable || !editable}
          onMouseDown={handleStageMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onDragEnd={(e) => {
            if (e.target === e.target.getStage()) {
              setStagePos({ x: e.target.x(), y: e.target.y() });
            }
          }}
        >
          <Layer>
            {/* Grid */}
            {Array.from({ length: Math.ceil(2000 / GRID_SIZE) }).map((_, i) => (
              <React.Fragment key={`grid-${i}`}>
                <Line points={[i * GRID_SIZE, 0, i * GRID_SIZE, 2000]} stroke="#f3f4f6" strokeWidth={0.5} />
                <Line points={[0, i * GRID_SIZE, 2000, i * GRID_SIZE]} stroke="#f3f4f6" strokeWidth={0.5} />
              </React.Fragment>
            ))}

            {/* Walls / Roads */}
            {walls.map((wall, i) => (
              <WallLine key={`wall-${i}`} wall={wall} editable={editable} onDelete={() => handleDeleteWall(i)} />
            ))}

            {/* Drawing preview */}
            {drawing && (
              <Line
                points={[drawing.x1, drawing.y1, drawing.x2, drawing.y2]}
                stroke={drawing.type === 'road' ? '#9ca3af' : lineColor}
                strokeWidth={drawing.type === 'road' ? 12 : lineWidth}
                dash={drawing.type === 'road' ? [12, 8] : undefined}
                opacity={0.7}
              />
            )}

            {/* Tree markers */}
            {markers.map((marker, i) => (
              <TreeMarker
                key={`tree-${i}`}
                marker={marker}
                index={i}
                editable={editable}
                isSelected={selectedId === `tree-${i}`}
                onSelect={() => editable && setSelectedId(`tree-${i}`)}
                onDelete={() => handleDeleteMarker(i)}
                onTransform={(newScale) => handleMarkerTransform(i, newScale)}
              />
            ))}

            {/* Gates */}
            {gates.map(gate => (
              <GateNode
                key={gate.id}
                gate={gate}
                editable={editable}
                color={getGateColor(gate)}
                openSeconds={getOpenSeconds(gate)}
                showLiveTimer={showLiveTimer}
                isBulkSelected={selectedGateIds.includes(gate.id)}
                isSelected={selectedId === `gate-${gate.id}`}
                statusRingColor={statusRingColorFor ? statusRingColorFor(gate) : null}
                onSelect={() => editable && setSelectedId(`gate-${gate.id}`)}
                onDragEnd={(e) => handleGateDragEnd(gate, e)}
                onClick={() => {
                  if (editable) {
                    setSelectedId(`gate-${gate.id}`);
                  } else if (onGateClick) {
                    onGateClick(gate);
                  }
                }}
                onResize={onGateResize}
                onDelete={onGateDelete ? () => onGateDelete(gate) : null}
                deletable={onGateDelete && (gateDeletable ? gateDeletable(gate) : true)}
              />
            ))}
          </Layer>
        </Stage>

        <CanvasControls
          onZoomIn={() => zoomBy(1.2)}
          onZoomOut={() => zoomBy(1 / 1.2)}
          onReset={resetView}
          isFullscreen={isFullscreen}
          onToggleFullscreen={() => setIsFullscreen(v => !v)}
        />
      </div>

      <div className="map-legend">
        <span><span className="status-dot green" style={{ display: 'inline-block', marginLeft: '0.3rem' }}></span> תקין</span>
        <span><span className="status-dot yellow" style={{ display: 'inline-block', marginLeft: '0.3rem' }}></span> תשומת לב</span>
        <span><span className="status-dot red" style={{ display: 'inline-block', marginLeft: '0.3rem' }}></span> פתוח / קריטי</span>
        <span className="map-legend-mouse-hints">גרירה = הזזת מפה | + / - = זום | ⛶ = מסך מלא</span>
      </div>
    </div>
  );
}

function CanvasControls({ onZoomIn, onZoomOut, onReset, isFullscreen, onToggleFullscreen }) {
  const btn = {
    width: '32px',
    height: '32px',
    border: '1px solid #e5e7eb',
    background: '#ffffff',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '1rem',
    color: '#1a1a2e',
    fontFamily: 'Heebo, sans-serif',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    padding: 0,
    lineHeight: 1,
  };
  return (
    <div style={{
      position: 'absolute',
      bottom: '12px',
      right: '12px',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      zIndex: 5,
    }}>
      <button type="button" onClick={onToggleFullscreen} title={isFullscreen ? 'יציאה ממסך מלא (Esc)' : 'מסך מלא'} style={btn}>
        {isFullscreen ? '⛶' : '⛶'}
      </button>
      <div style={{ height: '4px' }} />
      <button type="button" onClick={onZoomIn} title="הגדל" style={btn}>+</button>
      <button type="button" onClick={onZoomOut} title="הקטן" style={btn}>−</button>
      <button type="button" onClick={onReset} title="איפוס תצוגה" style={{ ...btn, fontSize: '0.85rem' }}>⟲</button>
    </div>
  );
}

function GateNode({
  gate, editable, color, isSelected, onSelect, onDragEnd, onClick, onResize,
  openSeconds = 0, showLiveTimer = false, isBulkSelected = false,
  statusRingColor = null, onDelete = null, deletable = false,
}) {
  const shapeRef = useRef();
  const trRef = useRef();
  const textRef = useRef();
  const [nameHeight, setNameHeight] = useState(14);
  const x = gate.position_x || 100;
  const y = gate.position_y || 100;
  const scale = gate.gate_scale || 1;
  const w = DEFAULT_GATE_WIDTH;
  const iconX = (w - ICON_SIZE) / 2;
  const textY = ICON_SIZE + 6;
  const cardH = Math.max(DEFAULT_GATE_HEIGHT, textY + nameHeight + 4);

  const product = gate.product;
  // Dock-leveler gates draw a dedicated three-state icon (door open
  // + leveler position) instead of the catalog's open/closed pair —
  // the leveler is product-agnostic, comes from the live state, and
  // is what the operator actually needs to recognise on the map.
  const levelerUrl = levelerIconFor(gate);
  const iconUrl = levelerUrl
    || (product
      ? (gate.is_open ? product.category_icon_open_url : product.category_icon_closed_url)
      : null);
  const image = useHtmlImage(iconUrl);

  useEffect(() => {
    if (isSelected && editable && trRef.current && shapeRef.current) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected, editable]);

  useEffect(() => {
    if (textRef.current) {
      const measured = textRef.current.height();
      setNameHeight(prev => (measured && Math.abs(measured - prev) > 0.5) ? measured : prev);
    }
  }, [gate.name]);

  const handleTransformEnd = () => {
    if (!editable || !onResize || !shapeRef.current) return;
    const node = shapeRef.current;
    const newScale = Math.max(MIN_GATE_SCALE, Math.min(MAX_GATE_SCALE, node.scaleX()));
    node.scaleX(newScale);
    node.scaleY(newScale);
    onResize(gate.id, newScale);
  };

  // When the Shelli is silent past its threshold, dim the whole gate
  // and grey the status dot so the customer reads the data as stale.
  // We also suppress the live "minutes open" pill — the last is_open
  // we got is no longer trustworthy.
  const isOffline = !!gate.is_offline;
  // A gate that was never installed has no live state to trust yet, so
  // it must not read as open/closed. Paint its corner dot grey (neutral)
  // and suppress the open-timer / unauthorized-open visuals below.
  const stateUnknown = gate.installation_status === 'not_started';
  const cardOpacity = isOffline ? 0.45 : 1;
  const dotColor = (isOffline || stateUnknown) ? '#9ca3af' : color;

  // Unauthorized-open ring: gate is currently open AND outside its
  // active hours AND the customer opted in to unauthorized-open
  // alerts. Skipped when offline (we don't trust is_open in that
  // state) or while editing (admins moving icons don't want a red
  // alarm in their face).
  const showUnauthorizedRing = (
    !isOffline
    && !stateUnknown
    && !editable
    && gate.unauthorized_open_alert_enabled
    && gate.is_open
    && gate.is_in_active_hours === false
  );

  // The on-map open timer is opt-in per gate (live_timer_enabled).
  // Off by default — only gates the customer flagged (cold rooms, etc.)
  // show the running counter.
  const showTimer = !isOffline && !stateUnknown && showLiveTimer
    && gate.live_timer_enabled && gate.is_open && gate.opened_at;
  const timerLabel = showTimer ? formatOpenDuration(openSeconds) : '';
  const timerWidth = 44;
  const timerHeight = 18;
  const timerX = (w - timerWidth) / 2;
  const timerY = -timerHeight - 4;

  return (
    <>
      <Group
        ref={shapeRef}
        x={x}
        y={y}
        scaleX={scale}
        scaleY={scale}
        opacity={cardOpacity}
        draggable={editable}
        onDragEnd={onDragEnd}
        onClick={(e) => { e.cancelBubble = true; onClick(); if (onSelect) onSelect(); }}
        onTap={(e) => { e.cancelBubble = true; onClick(); if (onSelect) onSelect(); }}
        onTransformEnd={handleTransformEnd}
      >
        {/* Bulk-selection ring sits behind everything else */}
        {isBulkSelected && (
          <Rect
            x={-4} y={-4}
            width={w + 8} height={cardH + 8}
            stroke="#3b82f6"
            strokeWidth={2}
            cornerRadius={11}
            dash={[4, 3]}
          />
        )}
        {/* Unauthorized-open ring: solid red, attention-grabbing,
            distinct from the dashed blue selection ring. */}
        {showUnauthorizedRing && (
          <Rect
            x={-3} y={-3}
            width={w + 6} height={cardH + 6}
            stroke="#dc2626"
            strokeWidth={3}
            cornerRadius={11}
          />
        )}
        {/* Status ring (Phase 4 PR 3.B): used by the technician map
            to colour each gate by installation status. The map's
            normal live colours stay on the status dot. */}
        {statusRingColor && (
          <Rect
            x={-3} y={-3}
            width={w + 6} height={cardH + 6}
            stroke={statusRingColor}
            strokeWidth={3}
            cornerRadius={11}
          />
        )}
        <Rect width={w} height={cardH} fill="#ffffff" stroke="#e5e7eb" strokeWidth={1} cornerRadius={8} />
        {image ? (
          <KonvaImage image={image} x={iconX} y={2} width={ICON_SIZE} height={ICON_SIZE} />
        ) : (
          // No product assigned yet (a gate the customer just pinned, or
          // one awaiting the technician's model selection). Instead of a
          // blank box, label it plainly as "not defined" so it's obvious
          // this gate hasn't been set up yet.
          <Group x={iconX} y={2}>
            <Rect width={ICON_SIZE} height={ICON_SIZE} fill="#f9fafb" stroke="#e5e7eb" strokeWidth={1} cornerRadius={6} dash={[4, 3]} />
            <Text
              y={ICON_SIZE / 2 - 7}
              width={ICON_SIZE}
              text="לא מוגדר"
              align="center"
              fontSize={11}
              fontFamily="Heebo, sans-serif"
              fontStyle="600"
              fill="#9ca3af"
            />
          </Group>
        )}
        <Circle x={iconX + ICON_SIZE - 4} y={6} radius={5} fill={dotColor} stroke="#ffffff" strokeWidth={1.5} />
        {/* Fault indicator — small red warning triangle in the top-left
            of the icon area. Appears the moment Shelli reports a fault,
            independent of debounce: the email waits, the visual does not.
            Suppressed on leveler gates because S2 there is the leveler
            relay, not a fault signal — a flipping leveler must never
            paint a fault indicator. */}
        {gate.has_fault && !gate.is_continuous_with_leveler && (
          <>
            <Line
              points={[iconX + 7, 2, iconX + 1, 12, iconX + 13, 12]}
              closed
              fill="#dc2626"
              stroke="#ffffff"
              strokeWidth={1}
            />
            <Text
              x={iconX + 1}
              y={4}
              text="!"
              width={12}
              align="center"
              fontSize={9}
              fontStyle="700"
              fill="#ffffff"
            />
          </>
        )}
        <Text
          ref={textRef}
          x={0}
          y={textY}
          text={gate.name}
          fontSize={11}
          fontFamily="Heebo, sans-serif"
          fontStyle="600"
          fill="#1a1a2e"
          width={w}
          align="center"
        />
        {/* Live "minutes open" pill — colored by threshold ratio */}
        {showTimer && (
          <Group x={timerX} y={timerY}>
            <Rect
              width={timerWidth}
              height={timerHeight}
              fill={color}
              cornerRadius={9}
            />
            <Text
              x={0}
              y={3}
              text={timerLabel}
              fontSize={11}
              fontFamily="Heebo, sans-serif"
              fontStyle="700"
              fill="#ffffff"
              width={timerWidth}
              align="center"
            />
          </Group>
        )}
        {/* Delete badge — small red ✕ at the card's top-left, shown only
            while this gate is selected in edit mode AND the parent says
            it's deletable (customer: only un-installed gates). Stops the
            click from bubbling into drag / re-select. */}
        {editable && isSelected && deletable && onDelete && (
          <Group
            x={-2}
            y={-2}
            onClick={(e) => { e.cancelBubble = true; onDelete(); }}
            onTap={(e) => { e.cancelBubble = true; onDelete(); }}
          >
            <Circle radius={9} fill="#dc2626" stroke="#ffffff" strokeWidth={1.5} />
            <Text
              x={-9}
              y={-6}
              width={18}
              text="✕"
              align="center"
              fontSize={11}
              fontStyle="700"
              fill="#ffffff"
            />
          </Group>
        )}
      </Group>
      {isSelected && editable && (
        <Transformer
          ref={trRef}
          rotateEnabled={false}
          enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right']}
          boundBoxFunc={(oldBox, newBox) => {
            if (newBox.width < 30 || newBox.height < 20) return oldBox;
            if (newBox.width > DEFAULT_GATE_WIDTH * MAX_GATE_SCALE) return oldBox;
            return newBox;
          }}
          anchorSize={8}
          anchorCornerRadius={2}
          anchorFill="#3b82f6"
          anchorStroke="#ffffff"
          borderStroke="#3b82f6"
          borderStrokeWidth={1}
          borderDash={[4, 4]}
        />
      )}
    </>
  );
}

function TreeMarker({ marker, index, editable, isSelected, onSelect, onDelete, onTransform }) {
  const shapeRef = useRef();
  const trRef = useRef();
  const [hovered, setHovered] = useState(false);
  const scale = marker.scale || 1;

  useEffect(() => {
    if (isSelected && editable && trRef.current && shapeRef.current) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected, editable]);

  const handleTransformEnd = () => {
    if (!shapeRef.current) return;
    const node = shapeRef.current;
    const newScale = Math.max(0.3, Math.min(5, node.scaleX()));
    node.scaleX(newScale);
    node.scaleY(newScale);
    if (onTransform) onTransform(newScale);
  };

  return (
    <>
      <Group
        ref={shapeRef}
        x={marker.x}
        y={marker.y}
        scaleX={scale}
        scaleY={scale}
        draggable={editable}
        onClick={(e) => { e.cancelBubble = true; if (onSelect) onSelect(); }}
        onTap={(e) => { e.cancelBubble = true; if (onSelect) onSelect(); }}
        onDblClick={() => editable && onDelete()}
        onDblTap={() => editable && onDelete()}
        onMouseEnter={() => editable && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onTransformEnd={handleTransformEnd}
      >
        <Rect x={-2} y={0} width={4} height={10} fill="#a16207" />
        <Ellipse x={0} y={-5} radiusX={10} radiusY={10} fill={hovered && editable ? '#ef4444' : '#22c55e'} opacity={0.7} />
      </Group>
      {isSelected && editable && (
        <Transformer
          ref={trRef}
          rotateEnabled={false}
          enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right']}
          boundBoxFunc={(oldBox, newBox) => {
            if (newBox.width < 8 || newBox.height < 8) return oldBox;
            return newBox;
          }}
          anchorSize={6}
          anchorCornerRadius={2}
          anchorFill="#22c55e"
          anchorStroke="#ffffff"
          borderStroke="#22c55e"
          borderStrokeWidth={1}
          borderDash={[3, 3]}
        />
      )}
    </>
  );
}

function WallLine({ wall, editable, onDelete }) {
  const [hovered, setHovered] = useState(false);
  const isDash = wall.dash;
  const color = wall.color || '#1a1a2e';
  const width = wall.width || 3;

  return (
    <Line
      points={[wall.x1, wall.y1, wall.x2, wall.y2]}
      stroke={hovered && editable ? '#ef4444' : color}
      strokeWidth={hovered && editable ? width + 2 : width}
      dash={isDash ? [12, 8] : undefined}
      hitStrokeWidth={20}
      onMouseEnter={() => editable && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDblClick={() => editable && onDelete()}
      onDblTap={() => editable && onDelete()}
    />
  );
}

function ToolButton({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '0.3rem 0.75rem', borderRadius: '6px',
        border: `1px solid ${active ? '#1a1a2e' : '#e5e7eb'}`,
        background: active ? '#1a1a2e' : '#ffffff',
        color: active ? '#ffffff' : '#4a5568',
        fontSize: '0.8rem', fontFamily: 'Heebo, sans-serif', fontWeight: 500,
        cursor: 'pointer', transition: 'all 0.15s',
      }}
    >
      {label}
    </button>
  );
}

export default SiteMapCanvas;
